import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import {
  shouldShowDemoLogin,
  shouldShowLocalSocialLoginStubs,
} from './loginDemoVisibility';
import './Auth.css';

const showDemoLogin = shouldShowDemoLogin(process.env);
const showLocalSocialLoginStubs = shouldShowLocalSocialLoginStubs(process.env);

export default function Login() {
  const { login, demoLogin } = useAuth();
  const { language, setLanguage, copy } = useLanguage();
  const auth = copy.auth;
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : '/';
  const [form, setForm] = useState({ email: '', password: '' });
  const [remember, setRemember] = useState(true);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(location.state?.guardMessage || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!consent) {
      setError(auth.consentRequired);
      return;
    }
    setLoading(true);
    try {
      await login(form.email, form.password);
      if (remember) localStorage.setItem('remember_email', form.email);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.code === 'ECONNABORTED' ? auth.loginTimeout : err.response?.data?.error || auth.loginFailed);
    } finally {
      setLoading(false);
    }
  };

  const useDemo = async () => {
    setError('');
    setNotice('');
    if (!consent) {
      setError(auth.consentRequired);
      return;
    }
    setLoading(true);
    try {
      const data = await demoLogin();
      if (remember && data.user?.email) localStorage.setItem('remember_email', data.user.email);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.code === 'ECONNABORTED' ? auth.loginTimeout : err.response?.data?.error || auth.demoFailed);
    } finally {
      setLoading(false);
    }
  };

  const showNotice = (message) => {
    setError('');
    setNotice(message);
    window.setTimeout(() => setNotice(''), 2600);
  };

  const hero = language === 'zh' ? {
    title: '让技能、项目和收入路径更清晰',
    text: '登录后继续管理服务机会、订单、消息和财务，像使用专业职业导航一样推进每一次协作。',
    statOne: '一站式',
    statOneLabel: '服务与需求管理',
    statTwo: '安全',
    statTwoLabel: '平台内沟通付款',
    formTitle: '欢迎回来！',
    formSubtitle: '请输入账号信息继续使用 Oblitrok。',
  } : {
    title: 'Make skills, projects, and income paths clear',
    text: 'Sign in to manage opportunities, orders, messages, and payments in one focused workspace.',
    statOne: 'Unified',
    statOneLabel: 'Services and requirements',
    statTwo: 'Secure',
    statTwoLabel: 'In-platform payments',
    formTitle: 'Welcome back!',
    formSubtitle: 'Please enter your details to continue.',
  };

  return (
    <div className="auth-page career-auth-page">
      <header className="auth-topbar career-auth-topbar">
        <Link to="/" className="auth-brand">Oblitrok</Link>
        <div className="language-switch language-switch-light auth-language" aria-label={copy.nav.language}>
          <button type="button" className={language === 'en' ? 'active' : ''} onClick={() => setLanguage('en')}>EN</button>
          <button type="button" className={language === 'zh' ? 'active' : ''} onClick={() => setLanguage('zh')}>中文</button>
        </div>
      </header>
      <main className="career-auth-shell">
        <section className="career-auth-hero">
          <div className="career-hero-copy">
            <span className="career-hero-eyebrow">Oblitrok</span>
            <h1>{hero.title}</h1>
            <p>{hero.text}</p>
          </div>
          <div className="career-hero-stats">
            <div><strong>{hero.statOne}</strong><span>{hero.statOneLabel}</span></div>
            <div><strong>{hero.statTwo}</strong><span>{hero.statTwoLabel}</span></div>
          </div>
        </section>

        <section className="career-auth-panel">
          <div className="auth-card career-auth-card">
            <div className="career-card-title">
              <h2>{hero.formTitle}</h2>
              <p>{hero.formSubtitle}</p>
            </div>
            <form onSubmit={handleSubmit}>
              <label className="auth-field">
                <span className="auth-field-label">{auth.usernameOrEmail}</span>
                <input
                  type="text"
                  autoComplete="username"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder={auth.usernameOrEmail}
                />
              </label>
              <label className="auth-field">
                <span className="auth-field-label">{auth.password}</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder={auth.password}
                />
              </label>
              <label className="remember-row">
                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
                <span>{auth.remember}</span>
                <Link to="/forgot-password">{auth.forgot}</Link>
              </label>
              <label className="auth-consent">
                <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
                <span className="auth-consent-copy">
                  {auth.consentPrefix}{' '}
                  <Link to="/terms">{auth.termsLink}</Link>{' '}
                  {auth.consentJoiner}{' '}
                  <Link to="/privacy-policy">{auth.privacyLink}</Link>.
                </span>
              </label>
              {error && <p className="error-msg">{error}</p>}
              <button type="submit" className="auth-primary" disabled={loading}>
                {loading ? auth.loggingIn : auth.continue}
              </button>
            </form>

            <div className="auth-divider"><span>{auth.or}</span></div>

            {notice && <p className="auth-inline-note">{notice}</p>}
            {showLocalSocialLoginStubs && (
              <>
                <button type="button" className="social-btn google-btn" onClick={() => showNotice(auth.googleUnavailable || 'Third-party login is not connected yet.')}>
                  <span className="google-mark">G</span>
                  {auth.google}
                </button>
                <button type="button" className="social-btn apple-btn" onClick={() => showNotice(auth.appleUnavailable || 'Third-party login is not connected yet.')}>
                  <span>Apple</span>
                  {auth.apple}
                </button>
              </>
            )}
            {showDemoLogin && (
              <button type="button" className="demo-login-btn" onClick={useDemo} disabled={loading}>
                {auth.demo}
              </button>
            )}

            <div className="auth-card-bottom">
              <p>{auth.noAccount}</p>
              <Link to="/register" className="auth-secondary">{auth.signup}</Link>
            </div>
          </div>
        </section>
      </main>
      <footer className="auth-footer">
        <span>{auth.footer}</span>
      </footer>
    </div>
  );
}
